import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { apiURL } from '../../../utilities/URLs'
import { ClickId, company, CustomerContact } from '../../../utilities/CustomerData'
import { useChartConnect } from '@chartapi/react-connect'
import TagManager from 'react-gtm-module'
import { getCompanyGTMID, getGTMDataLayerBody } from '../../../utilities/extensions'
import posthog from 'posthog-js'

export function ChartConnect({ setReload }) {
  const { contactId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const isReconnect = queryParams.get('isReconnect')
  const isSwitch = queryParams.get('isSwitch')

  const [contact, setContact] = useState(new CustomerContact())
  const [clickId, setClickId] = useState(new ClickId())
  const [flowType, setFlowType] = useState(isSwitch ? 'full-irs-online-account' : 'irs-8821-personal')
  const [showError, setShowError] = useState(false)
  const [sendingChartCode, setSendingChartCode] = useState(false)

  const onSuccess = ({ code }) => {
    console.log(code)
    handleUploadChartCode(code)
  }
  const onError = ({ errorMessage }) => {
    console.log(errorMessage)
    posthog.capture('chart_error', {
      error_message: errorMessage
    })
    setShowError(true) 
  }
  const onClose = () => console.log('User exited Chart Connect')

  const { open } = useChartConnect({
    clientId: process.env.REACT_APP_CHART_CLIENT_ID,
    zIndex: 999,
    sandbox: false,
    flow: flowType,
    onSuccess,
    onError,
    onClose
  })

  const companyGTM = getCompanyGTMID(company.companyId)
  if(companyGTM) {
    TagManager.initialize({
      gtmId: companyGTM
    })
  }

  const handleUploadChartCode = async (code) => {
    setSendingChartCode(true)

    const chartAccessTokenResponse = await fetch(`${apiURL}/api/uploadChartCode?contactId=${encodeURIComponent(contactId)}&code=${encodeURIComponent(code)}&isAuth=${encodeURIComponent(flowType === 'irs-8821-personal' ? 1 : 0)}&isReconnect=${encodeURIComponent(isReconnect ? 1 : 0)}&isSwitch=${encodeURIComponent(isSwitch ? 1 : 0)}`)
    const chartAccessTokenData = await chartAccessTokenResponse.json()
    setSendingChartCode(false)

    if (chartAccessTokenData.response.error) {
      console.error(chartAccessTokenData.response.error)
      setShowError(true)
    } else {
      if (flowType === 'irs-8821-personal') {
        setFlowType('full-irs-online-account')
        if(companyGTM)
          TagManager.dataLayer(getGTMDataLayerBody(company.companyId, clickId, 'submitted'))
      } else
        navigate(`/calculateRefund/${chartAccessTokenData.response.jwtToken}`)
    }
  }

  useEffect(() => {
    const handleGetLeadContact = async () => {
      const contactResponse = await fetch(`${apiURL}/api/getLeadContact?contactId=${encodeURIComponent(contactId)}`)
      const contactData = await contactResponse.json()
      if (contactData.response.error)
        console.error(contactData.response.error)
      else {
        const contact = new CustomerContact(contactData.response.contact)
        setClickId(new ClickId(contactData.response.clickId))
        setContact(contact)
        posthog.identify(contact.contactId, {
          name: contact.contactName,
          email: contact.contactEmail
        })
        if (isReconnect || !contact.chartAccessToken)
          open()
      }
    }

    document.title = `${company.companyName} | Chart Connect`
    handleGetLeadContact()

    posthog.startSessionRecording()
    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  return (
    <div className='w-screen h-screen flex flex-col items-center justify-center gap-10'>
      <img className='w-72' src={company.companyLogoURL} />
      {(!isReconnect && !isSwitch) && (
        <div className='w-full max-w-lg max-sm:w-11/12 flex flex-col items-center gap-2'>
          <div className='w-full bg-gray-200 h-16 rounded-full overflow-hidden relative'>
            <div className='h-full bg-[#C91515] w-[85%] rounded-r-full'></div>
            <span className='absolute inset-0 flex justify-center text-2xl items-center font-bold text-white'>
              85% Complete
            </span>
          </div>
        </div>
      )}
      <div className='bg-white p-10 rounded-lg shadow-xl max-w-lg max-sm:w-11/12 w-full'>
        {flowType === 'irs-8821-personal' ? (
          <div className='w-full flex flex-col items-center gap-4'>
            {(!isReconnect && !isSwitch) && (
              <div className='w-full flex flex-col bg-white p-4 rounded-md shadow-md gap-4'>
                <p className='text-sm text-center text-black'>See QUICK Video Below For Instructions on NEXT STEPS!</p>
                <iframe className='w-full aspect-video' src="https://www.loom.com/embed/a38c80c59cc2489399a5ac55af61a718?sid=5514da5c-e1f0-4170-9915-10161385741a&hideEmbedTopBar=true" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe>
              </div>
            )}
            <p className='font-bold text-3xl text-center text-gray-800'>Alright {contact.contactFirstName}, let's connect your IRS documents.</p>
            <div className='w-full flex flex-col'>
              <button disabled={!isReconnect && (contact.chartAccessToken || sendingChartCode)} onClick={() => open()} className={`w-full py-3 text-white font-semibold rounded-lg shadow-md transform transition-all ${!isReconnect && (contact.chartAccessToken || sendingChartCode) ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                Claim Refund
              </button>
              {(contact.chartAccessToken && !isReconnect) && (
                <div className='flex flex-col items-center gap-4'>
                  <p className='text-center'>Your IRS Documents have been requested.</p>
                  <button onClick={() => { setReload(true); navigate('/') }} className='px-10 py-3 text-white font-semibold rounded-lg shadow-md transform bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                    Return to Customer Portal
                  </button>
                </div>
              )}
              {sendingChartCode && (
                <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>Submitting IRS Data...</p>
                </div>
              )}
              {showError && (
                <p className='text-center text-red-600'>Oops! We ran into an issue submitting your IRS information. Please retry again in 10 minutes. Thanks for your patience!</p>
              )}
            </div>
          </div>
        ) : (
          !showError ? (
            <div className='w-full flex flex-col items-center gap-4'>
              <p className='font-bold text-2xl'>Final Application Steps</p>
              <div className='w-full flex flex-col items-start gap-4'>
                <p>We have successfully submitted your IRS Form 8821 authorization to the IRS! Here's what you need to do next:</p>
                <p className='font-bold'>Next Steps:</p>
                <ul className="list-disc pl-5">
                  <li>Click the button below to <span className='font-bold'>access your Customer Portal</span> and <span className='font-bold'>set a password</span> when prompted.</li>
                  <li>Inside the portal, click the <span className='font-bold'>"Complete Your Calculation"</span> button to complete your application.</li>
                </ul>
                <p className='font-bold'>Why This Step is Important:</p>
                <ul className="list-disc pl-5">
                  <li>Your <span className='font-bold'>ID.me login</span> is the same as your <span className='font-bold'>IRS.gov online account</span> credentials.</li>
                  <li>Logging in through <span className='font-bold'>ID.me</span> creates a <span className='font-bold'>one-time, temporary, secure token</span> to sync your <span className='font-bold'>IRS tax records</span> for <span className='font-bold'>immediate processing.</span></li>
                </ul>
                <p><span className='font-bold'>Express Refund Program:</span> If you're eligible, you can <span className='font-bold'>receive your funds in as little as 5-7 days</span> after completing this final step!</p>
                <p className='font-bold'>Click below to continue:</p>
              </div>
              <div className='w-full flex flex-col gap-2'>
                <button
                  onClick={() => { setReload(true); navigate('/') }}
                  disabled={sendingChartCode}
                  className={`w-full py-2 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 ${sendingChartCode ? 'bg-gray-400 cursor-not-allowed' : 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200'}`}>
                  Go To Customer Portal
                </button>
              </div>
              {sendingChartCode && (
                <div className='flex flex-row items-center justify-center gap-2 -mb-5'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                  <p>Grabbing IRS Data, Please Wait...</p>
                </div>
              )}
            </div>
          ) : (
            <div className='w-full flex flex-col items-center gap-4'>
              <p className='font-bold text-2xl'>Oops!</p>
              <p>We ran into an issue while trying to sync your Authorization Form. Please return to your customer portal and try again in about 10 minutes. Thanks for your patience!</p>
              <div className='w-full flex flex-col gap-2'>
                <button
                  onClick={() => { setReload(true); navigate('/') }}
                  className='w-full py-2 text-white rounded focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 max-sm:mb-4 bg-primaryColor hover:bg-primaryDarkColor transition duration-200'>
                  Go To Customer Portal
                </button>
              </div>
            </div>
          )
        )}
      </div>
    </div>
  )
}